/**
 * GA4 METRIC REGISTRY — WHICH METRIC MAY BE STORED ON WHICH KIND OF ROW.
 * PURE, a LEAF (no imports), shared by the pull and the future read path.
 *
 * ⚠️⚠️ READ THIS BEFORE ADDING A METRIC TO ANY GA4 REPORT. The rule this file
 * enforces was missed once already, in the brief that designed the pull
 * (2026-09-25), and the miss would have shipped per-location session counts
 * inflated by however many of a studio's pages the average visitor opens.
 *
 * THE MECHANISM: GA4 attributes a metric to a dimension value, and whether rows
 * split by that dimension can be ADDED depends on the metric's SCOPE:
 *
 *   EVENT-scoped (screenPageViews, eventCount, keyEvents, userEngagementDuration)
 *     Each event happens on exactly one page. Rows split by page — or by anything
 *     else — partition the events, so they sum exactly: across pages, across the
 *     pages of a location, across locations, across dates.
 *
 *   SESSION-scoped (sessions, engagedSessions, and averageSessionDuration, which
 *   is stored as a TOTAL — see below)
 *     `sessions × pagePath` means "sessions in which THAT page was viewed". A
 *     visitor who opens /rolleston and then /rolleston/classes appears on BOTH
 *     rows. So page-path rows DO NOT SUM — not across a location's pages, not
 *     across the site. What DOES sum:
 *       - `sessions × landingPage` — every session has exactly ONE landing page,
 *         so landing-page rows partition sessions and sum exactly.
 *       - a FILTERED request (pagePath matches any of a location's pages): GA4
 *         filters the events, then counts each session once. That is what the old
 *         GA4 UI filter did, and it is the only correct per-location number.
 *
 *   FIRST-VISIT-scoped (newUsers)
 *     Counts the first_visit event, which fires on the LANDING page. Exact per
 *     landing page; its meaning under a page-path filter is "new users whose
 *     first visit landed on a matching page", which is not what anybody reading
 *     "new users at Rolleston" would assume — so it is not stored there.
 *
 * ⚠️⚠️ THE ROWS A GIVEN METRIC IS ALLOWED ON (`metricAllowedOn`):
 *
 *   | row kind                  | event | session | newUsers |
 *   |---------------------------|-------|---------|----------|
 *   | site  (no page dimension) |  ✅   |   ✅    |    ✅    |
 *   | page  × landingPage       |  ✅   |   ✅    |    ✅    |
 *   | page  × pagePath          |  ✅   |   ❌    |    ❌    |
 *   | location (pagePath filter)|  ✅   |   ✅    |    ❌    |
 *
 * ⚠️⚠️ A NON-SUMMABLE METRIC IS NOT REQUESTED AT ALL — NOT STORED-AND-LABELLED
 * (Scott, 2026-09-27). A number that must never be summed, sitting in a table of
 * numbers that are, WILL be summed eventually, and the label explaining why not
 * will be three files away when it happens. Nothing is lost: "top landing pages
 * by sessions" comes from the landingPage rows, which are exact, and page-path
 * sessions have no use case.
 *
 * ⚠️⚠️ LOCATION ROWS STILL DO NOT SUM ACROSS LOCATIONS. A session that viewed both
 * /rolleston and /queenstown is counted once in EACH location's filtered request.
 * The filter fixed the WITHIN-location overlap; it cannot fix the BETWEEN-location
 * one, and no GA4-only approach can (choosing a single winner needs a tag change,
 * which is out of scope permanently).
 *
 * ⚠️⚠️ THE REPORT RULE (strengthened 2026-09-27): PER-LOCATION FIGURES CANNOT BE
 * ADDED UP — not to a group total, not to a site total, not to "all studios" —
 * and a location is NEVER shown as a share of the site total. Both hold whatever
 * the measured overlap reads on a given day.
 *
 * ⚠️⚠️ AND DO NOT JUDGE THE OVERLAP FROM Σ(per-location) ÷ SITE. The first live run
 * read 0.805, not the >1 the brief predicted, because two effects are in that
 * ratio and they cancel: the double-count pushes it up, and the ~⅓ of sessions
 * that never touch a studio page push it down. A reading NEAR 1.0 WOULD BE THE
 * DANGEROUS CASE, not the reassuring one — it would look like no overlap while
 * hiding exactly the overlap that makes summing wrong. That ratio is no longer
 * computed. The double-count is isolated against the UNION of all studios instead
 * (`measureDoubleCount` in ./locations.ts): one extra filtered request counts
 * each session once, so Σ − union is the excess. That gives the MAGNITUDE only.
 * WHICH studio a two-studio session "belongs" to is unknowable from GA4 data, and
 * that is why the summing rule above is absolute.
 *
 * ⚠️ NOT STORED AT ALL, anywhere:
 *   - `activeUsers` — counts unique PEOPLE, so it does not sum across dates, pages,
 *     locations or breakdown values. A daily row of it summed to a month counts a
 *     returning visitor once per day. Same reasoning as Meta `reach`, which the
 *     demographics pull excludes for the identical reason.
 *     ⚠️⚠️ THE RULE FOR ITS RETURN (Scott, 2026-09-27): activeUsers may only come
 *     from a request whose date range IS the period displayed. Never stored
 *     per-day and summed — not per day, not per week, not per any bucket that a
 *     reader then adds up. That is a NEW REPORT SHAPE (one request per displayed
 *     period, re-requested when the period changes), not a column added to the
 *     daily reports, so it needs an explicit decision before it comes back. Do
 *     not add `activeUsers` to GA4_METRICS to "fill the gap".
 *   - `bounceRate` — a RATIO. Stored as its numerator and denominator
 *     (`engagedSessions`, `sessions`) and derived per bucket by `deriveBounceRate`.
 *     Storing the rate invites averaging it, which is wrong for the same reason
 *     averaging cost-per-lead is wrong (see deriveCostPerLead in series.ts).
 *   - `averageSessionDuration` — also a ratio; stored as the TOTAL
 *     `avg × sessions` in `sessionDurationSec`, so a period's average is
 *     Σtotal ÷ Σsessions.
 */

export type Ga4MetricName =
  | 'sessions'
  | 'engagedSessions'
  | 'newUsers'
  | 'averageSessionDuration'
  | 'userEngagementDuration'
  | 'screenPageViews'
  | 'eventCount'
  | 'keyEvents';

export type Ga4MetricScope = 'event' | 'session' | 'first-visit';

/** Which rows a stored GA4 row can be. `site` has no page dimension at all. */
export type Ga4RowScope = 'site' | 'page' | 'location';
export type Ga4PageDimension = 'pagePath' | 'landingPage';

export interface Ga4MetricDef {
  name: Ga4MetricName;
  scope: Ga4MetricScope;
  /** The stored field (and, snake-cased, the BigQuery column). */
  field: Ga4StoredMetricField;
}

export type Ga4StoredMetricField =
  | 'sessions'
  | 'engagedSessions'
  | 'newUsers'
  | 'sessionDurationSec'
  | 'userEngagementDurationSec'
  | 'screenPageViews'
  | 'eventCount'
  | 'keyEvents';

export const GA4_METRICS: readonly Ga4MetricDef[] = [
  { name: 'sessions', scope: 'session', field: 'sessions' },
  { name: 'engagedSessions', scope: 'session', field: 'engagedSessions' },
  { name: 'newUsers', scope: 'first-visit', field: 'newUsers' },
  // ⚠️ Requested as the average, STORED as the total (avg × sessions) — see header.
  { name: 'averageSessionDuration', scope: 'session', field: 'sessionDurationSec' },
  { name: 'userEngagementDuration', scope: 'event', field: 'userEngagementDurationSec' },
  { name: 'screenPageViews', scope: 'event', field: 'screenPageViews' },
  { name: 'eventCount', scope: 'event', field: 'eventCount' },
  { name: 'keyEvents', scope: 'event', field: 'keyEvents' },
];

export function ga4Metric(name: Ga4MetricName): Ga4MetricDef {
  const def = GA4_METRICS.find((m) => m.name === name);
  if (!def) throw new Error(`Unknown GA4 metric ${name}`);
  return def;
}

/**
 * THE ONE PLACE THE SUMMABILITY TABLE IN THE HEADER IS ENFORCED. The pull asks
 * this for every metric of every request, so a report spec can list a metric
 * freely and it is simply not requested on a row kind it would be wrong on.
 */
export function metricAllowedOn(
  metric: Ga4MetricName,
  scope: Ga4RowScope,
  pageDimension: Ga4PageDimension | null,
): boolean {
  const s = ga4Metric(metric).scope;
  if (s === 'event') return true;
  if (scope === 'site') return true;
  if (scope === 'page') return pageDimension === 'landingPage';
  // location: a pagePath-filtered request.
  return s === 'session';
}

/**
 * Bounce rate for one bucket, from that bucket's own sessions and engaged
 * sessions. ⚠️ NULL WITH NO SESSIONS, never 0% — a printed zero reads as a
 * measurement. Never average this across buckets; sum the inputs first.
 */
export function deriveBounceRate(sessions: number | null, engagedSessions: number | null): number | null {
  if (sessions === null || engagedSessions === null || sessions <= 0) return null;
  return 1 - engagedSessions / sessions;
}
